import { sendAppEvent } from "src/Utils";
import type { CTAActionType } from "./ThinkingStep/cta-types";

/**
 * Payload shape for the legacy "Story Bible Generate" Mixpanel event.
 *
 * Mirrors the props sent by `AppEventPublisher.storyBible.generate(...)` and
 * `AppEventPublisher.storyBible.{characters,worldbuilding}.generateTrait(...)`
 * from the pre-agentic-chat code paths.
 */
export type StoryBibleGenerateEvent = {
  type: string;
  target?: "characters" | "worldbuilding";
  generateFromBlank?: boolean;
};

/**
 * Maps an agentic-chat CTA action type to the legacy "Story Bible Generate"
 * Mixpanel event payload that the corresponding pre-agentic-chat code path
 * used to fire.
 *
 * Returns `null` for CTA types that never had a corresponding legacy event
 * (e.g. `rewrite_synopsis` was tracked only via tour signals, not via the
 * "Story Bible Generate" event).
 *
 * Why this exists: when the agentic chat beta gate was removed, Story Bible
 * "Generate"/"Rewrite" button clicks started flowing through `trigger_cta_chat`
 * instead of the legacy generation endpoints. The legacy paths were the only
 * call sites of `AppEventPublisher.storyBible.generate(...)`, so historical
 * Mixpanel charts that filter the "Story Bible Generate" event by `type`
 * suddenly dropped off. Dual-firing the legacy event from the CTA handler
 * restores those charts.
 *
 * Synopsis-only: Story Bible synopsis still invokes `AppEventPublisher` inside
 * `StoryBible/Textarea` before the parent `generate()`, and the blank-field
 * `StoryBible/Header` shortcut does so after `generate()` returns truthy. Agentic
 * synopsis passes `skipStoryBibleGenerateAnalyticsInvoke` from `CopyableTextarea`
 * (`ctaEnabled`) into both so we do not emit the synopsis event twice.
 */
export function getStoryBibleGenerateEventForCTA(
  ctaType: CTAActionType,
): StoryBibleGenerateEvent | null {
  switch (ctaType) {
    case "generate_chapter":
      return { type: "outline chapter" };
    case "rewrite_chapter":
      return { type: "rewrite outline chapter" };
    case "generate_outline":
      return { type: "outline" };
    case "rewrite_outline":
      return null;
    case "generate_synopsis":
      return { type: "synopsis" };
    case "generate_character":
      return { type: "characters" };
    case "generate_worldbuilding":
      return { type: "worldbuilding" };
    case "generate_cast":
      return { type: "character cast" };
    case "generate_trait":
      return { type: "trait regeneration", target: "characters", generateFromBlank: true };
    case "rewrite_trait":
      return { type: "trait regeneration", target: "characters", generateFromBlank: false };
    case "generate_worldbuilding_trait":
      return { type: "trait regeneration", target: "worldbuilding", generateFromBlank: true };
    case "rewrite_worldbuilding_trait":
      return { type: "trait regeneration", target: "worldbuilding", generateFromBlank: false };
    // rewrite_synopsis was never tracked via "Story Bible Generate" historically
    // (only via the STORY_BIBLE_SYNOPSIS_REWRITE_CLICKED tour signal), so we
    // intentionally do not dual-fire here.
    case "rewrite_synopsis":
      return null;
  }
}

/**
 * Side-effecting helper: fires the legacy "Story Bible Generate" Mixpanel
 * event for the given CTA action type, if one applies. No-op for CTA types
 * that never had a corresponding legacy event.
 */
export function publishStoryBibleGenerateForCTA(ctaType: CTAActionType): void {
  const payload = getStoryBibleGenerateEventForCTA(ctaType);
  if (payload) {
    void sendAppEvent("Story Bible Generate", payload);
  }
}

/**
 * Fires legacy "Story Bible Edited" analytics when agentic-chat CTA content is
 * applied. Rewrite outline used to emit this from the legacy REST panel on
 * success; the chat path must dual-fire here so edited tracking is preserved.
 */
export function publishStoryBibleEditedForCTAContentUpdate(ctaType: CTAActionType): void {
  switch (ctaType) {
    case "rewrite_outline":
      void sendAppEvent("Story Bible Edited", { type: "outline", action: "rewrite" });
      return;
    default:
      return;
  }
}
